import React, { FC, useCallback, useState } from 'react';
import { AlertColor, Typography, useTheme } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import strings from '../../../common/Translation/Translate';
import {
  fetchUserContext,
  parentOrgInitData,
  setAuthTokens,
} from '../../../utils/redux/reducer/authentication-slice';
import { identifiers } from '../../../utils/constants/identifiers';
import { useCustomNavigate } from 'utils/hooks/useCustomNavigate';
import { routes } from 'utils/constants/routes';
import AuthPageShell, { AuthPageMessage } from '../AuthPageShell';
import EmailPasswordLogin from './EmailPasswordLogin';
import MobileEmailLogin from './MobileEmailLogin';

const LoginContent: FC = () => {
  const { loginTypes } = useSelector(parentOrgInitData);
  const shouldShowMobileLogin = loginTypes?.includes('MOBILE_OTP') || false;
  const Navigate = useCustomNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<AuthPageMessage>({
    display: false,
    severity: null,
    message: '',
  });

  const closeNotification = (value: boolean) => {
    setMessage(prev => ({ ...prev, display: value }));
  };

  const handleLoginSuccess = useCallback(
    async (data: any, identifier: string) => {
      const { firstTimeLogin, organization } = data;

      if (firstTimeLogin) {
        const accessToken = data.access_token ?? data.accessToken;
        const refreshTokenValue = data.refresh_token ?? data.refreshToken;
        Navigate(
          `/reset-password?email=${encodeURIComponent(identifier)}&refresh-token=${encodeURIComponent(
            refreshTokenValue ?? '',
          )}&access-token=${encodeURIComponent(
            accessToken ?? '',
          )}&firstTimeLogin=${firstTimeLogin}&orgId=${organization}`,
          { replace: true },
        );
        return;
      }

      dispatch(setAuthTokens(data));

      if (organization === null) {
        Navigate('/add-organization');
        return;
      }

      try {
        await dispatch(fetchUserContext() as any);
      } catch {
        // App will retry context on next page if needed.
      }
      Navigate(routes.dashboard, { replace: true });
    },
    [Navigate, dispatch],
  );

  return (
    <AuthPageShell
      title="Welcome back"
      message={message}
      onCloseNotification={closeNotification}>
      {shouldShowMobileLogin ? (
        <MobileEmailLogin
          language=""
          languageData={[]}
          theme={theme}
          setMessage={setMessage}
          Navigate={Navigate}
          dispatch={dispatch}
          loading={loading}
          setLoading={setLoading}
          handleLoginSuccess={handleLoginSuccess}
        />
      ) : (
        <EmailPasswordLogin
          setMessage={setMessage}
          loading={loading}
          setLoading={setLoading}
          handleLoginSuccess={handleLoginSuccess}
          Navigate={Navigate}
        />
      )}

      {shouldShowMobileLogin && (
        <Typography
          data-testid="forget"
          variant="body2"
          sx={{
            mt: 2,
            textAlign: 'center',
            color: theme.palette.primary.main,
            cursor: 'pointer',
            '&:hover': { textDecoration: 'underline' },
          }}
          onClick={() => Navigate('/forgot-password')}>
          {strings.forgotPassword}
        </Typography>
      )}
    </AuthPageShell>
  );
};

const Login: FC = () => (
  <GoogleReCaptchaProvider
    reCaptchaKey={identifiers.REACT_APP_GOOGLE_RECAPTCHA}
    scriptProps={{
      async: true,
      defer: true,
      appendTo: 'head',
    }}>
    <LoginContent />
  </GoogleReCaptchaProvider>
);

export default Login;
